import { GamePanel } from '@/components/Layouts/Layout'
import { useTranslation } from 'next-i18next/pages'
import {
  Box,
  Text,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Button,
  Flex,
  SimpleGrid,
  useMultiStyleConfig,
  useTab,
  Skeleton,
  Tr,
} from '@chakra-ui/react'
import { forwardRef } from 'react'
import { useQuery, NetworkStatus } from '@apollo/client'
import {
  GET_USER_STATS_DATA,
  LAST_JACKPOT_USER_GAMES_QUERY,
  LAST_ROULETTE_GAMES_QUERY,
  MINES_GAMES_HISTORY_QUERY,
  USER_BET_HISTORY,
} from '@/api/query'
import {
  GameZoneRow,
  GenericRow,
  MinesGameElement,
  Participants,
  SkeletonStyledTable,
  StyledTable,
  TableWrapper,
  UserBet,
} from '@/components/molecules/StyledTable/StyledTable'
import { NoResultsFallback } from '@/components/molecules/NoResultsFallback/NoResultsFallback'
import { transformDoubleGames } from '@/helpers/transformDoubleGames'
import { useRouter } from 'next/router'
import { tranformNftList } from '@/helpers/tranformNftList'
import { UserNfts } from '../../organisms/UserNfts'
import { UserLastWin } from '../../organisms/UserLastWin'
import { UserStatisticSummary } from '../../organisms/UserStatisticSummary'
import { UserProfileSummary } from '../../organisms/UserProfileSummary'
import { sections } from './sections'
import { UserProfile, UserStats } from './types'
import { NotFound } from '@/components/templates/NotFound/NotFound'
import { GetUserGamesQuery, GetUserStatsDataQuery } from '@/__generated__/graphql'
import { Game } from 'types/types'
import { BigNumberInWei } from '@injectivelabs/utils'

export const UserActivity = () => {
  const { t } = useTranslation()
  const { query } = useRouter()
  const address = query.address as string

  const { data, loading, error } = useQuery(GET_USER_STATS_DATA, {
    variables: { address },
  })

  if (error) {
    return <NotFound code="500" text={t('messages.something-went-wrong')} />
  }

  if (loading) {
    return (
      <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} columnGap={7} rowGap={8} mb={10}>
        <Skeleton startColor="ninja.600" endColor="ninja.700" rounded={'xl'} h={462} />
        <Skeleton startColor="ninja.600" endColor="ninja.700" rounded={'xl'} h={462} />
        <Skeleton startColor="ninja.600" endColor="ninja.700" rounded={'xl'} h={462} />
      </SimpleGrid>
    )
  }

  if (!data?.user) {
    return <NotFound />
  }

  const nfts = tranformNftList(data.nfts?.nodes)
  const userProfile = tranformUserProfile(data.user, address)
  const userStats = transformUserStats(data.userStatisticsV2)

  return (
    <>
      <UserStatistics nfts={nfts} user={userProfile} stats={userStats} />
      <UserGamesActivity address={address} />
    </>
  )
}

const transformUserStats = (data: GetUserStatsDataQuery['userStatisticsV2']) => {
  return {
    referralsCount: data?.activeReferralsCount || '0',
    totalGameCount: data?.betCount || '0',
    totalTopWinAmount: data?.maxPayout || '0',
    totalWinAmount: data?.payoutAmount || '0',
    totalWinCount: data?.payoutCount || '0',
  }
}

const UserGamesActivity = ({ address }: { address: string }) => {
  const { t } = useTranslation()
  const { query } = useRouter()
  const defaultIndex = sections.findIndex((s) => s.name === query.game)

  return (
    <>
      <Text fontSize={'4xl'} fontWeight={700} mb={4}>
        {t('common.activity')}
      </Text>
      <Tabs variant="unstyled" isLazy defaultIndex={defaultIndex >= 0 ? defaultIndex : 0}>
        <TabList
          bg={'none'}
          p={0}
          gap={3}
          display={'grid'}
          gridTemplateColumns={{ base: '1fr 1fr 1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)', xl: 'repeat(6, 1fr)' }}
          w={'full'}
        >
          {sections.map((section, index) => (
            <CustomGameTab key={index} icon={section.icon} title={section.title} />
          ))}
        </TabList>
        <TabPanels>
          <TabPanel px={0}>
            <DoubleGames address={address} />
          </TabPanel>
          <TabPanel px={0}>
            <GenericGames address={address} game={Game.RUNES} />
          </TabPanel>
          <TabPanel px={0}>
            <GenericGames address={address} game={Game.COINFLIP} />
          </TabPanel>
          <TabPanel px={0}>
            <MinesGames address={address} />
          </TabPanel>
          <TabPanel px={0}>
            <GenericGames address={address} game={Game.LOOTBOX} />
          </TabPanel>
          <TabPanel px={0}>
            <JackpotGames address={address} />
          </TabPanel>
          <TabPanel px={0}>
            <GenericGames address={address} game={Game.PLINKO} />
          </TabPanel>
          <TabPanel px={0}>
            <GenericGames address={address} game={Game.GAMEZONE} />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </>
  )
}

export const CustomGameTab = forwardRef<HTMLElement, { icon: React.ReactElement; title: string }>((props, ref) => {
  const tabProps = useTab({ ...props, ref })
  const isSelected = !!tabProps['aria-selected']

  const styles = useMultiStyleConfig('Tabs', tabProps)

  return (
    <Button __css={styles.tab} {...tabProps} border={'none'} pt={0} pl={0} pr={0} pb={0} bg={'none'}>
      <Box>
        <GamePanel
          wrapperStyles={{
            px: 3,
            pr: 4,
            py: 2,
            rounded: '12px',
          }}
          isActive={isSelected}
          icon={props.icon}
          title={props.title}
        />
      </Box>
    </Button>
  )
})

const UserStatistics = ({
  nfts,
  user,
  stats,
}: {
  nfts: {
    type: string
    amount: number
  }[]
  user: UserProfile
  stats: UserStats
}) => {
  return (
    <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} columnGap={7} rowGap={8} mb={10}>
      <UserProfileSummary user={user} nfts={nfts} />
      <UserStatisticSummary data={stats} />
      <UserLastWin address={user.address} />
      {!!nfts.length && <UserNfts nfts={nfts} />}
    </SimpleGrid>
  )
}

const tranformUserProfile = (data: GetUserStatsDataQuery['user'], address: string) => {
  return {
    profileUserName: data?.profileUserName || '',
    profileAvatar: data?.profileAvatar || '',
    created: data?.created || '',
    referrer: data?.referrer || '',
    address,
  }
}

const JackpotGames = ({ address }: { address: string }) => {
  const { t } = useTranslation()
  const { data, error, loading, networkStatus, fetchMore } = useQuery(LAST_JACKPOT_USER_GAMES_QUERY, {
    variables: {
      address,
    },
    notifyOnNetworkStatusChange: true,
  })

  const hasNextPage = data?.jackpotUserGames?.pageInfo.hasNextPage

  const handleLoadMore = () => {
    if (hasNextPage && networkStatus !== NetworkStatus.refetch) {
      fetchMore({
        variables: {
          cursor: data?.jackpotUserGames?.pageInfo.endCursor,
        },
      })
    }
  }

  if (error) {
    return <Text>{t('common.error-fetching-results')}</Text>
  }

  if (networkStatus === NetworkStatus.loading || !address) {
    return <SkeletonStyledTable />
  }

  const filteredGames = data?.jackpotUserGames?.edges.filter(({ node }) => !!node)

  const games = filteredGames?.map(({ node: game }) => {
    const bets = game!.jackpotBetsByGameId.nodes.map((bet) => ({
      address: bet!.address,
      amount: bet!.amount,
    }))

    const players = [...new Set(bets.map((bet) => bet.address))]

    const groupedBets = Object.entries(groupBy(bets, (bet) => bet.address!)).map(([address, bets]) => ({
      address,
      amount: bets.reduce((prev, curr) => new BigNumberInWei(curr.amount).plus(prev).toString(), '0'),
    }))

    const userBet = groupedBets.find((bet) => bet.address === address)!
    const isWon = game!.playerWinner === address
    const multiplier = isWon ? new BigNumberInWei(game?.winAmount).dividedBy(userBet.amount) : new BigNumberInWei(0)

    return {
      bet: {
        amount: userBet.amount,
        denom: Number(game?.id) <= 7655 ? USDC_NEW_DENOM : SHU_DENOM,
      },
      betId: '0',
      game: Game.JACKPOT,
      gameId: game?.id,
      multiplier: {
        isWon,
        number: multiplier.toFixed(2),
      },
      reward: {
        amount: isWon ? game?.winAmount : '0',
        denom: Number(game?.id) <= 7655 ? USDC_NEW_DENOM : SHU_DENOM,
      },
      widget: <Participants winner={game?.playerWinner!} opponents={players} />,
    } as UserBet
  })

  if (!games?.length) {
    return <NoResultsFallback text={t('messages.no-games')} />
  }

  return (
    <>
      <TableWrapper>
        {games.map((game, index) => {
          const isLastRow = index === games.length - 1
          return (
            <Tr key={game.gameId + '-' + game.betId}>
              <GenericRow isLastRow={isLastRow} {...game} />
            </Tr>
          )
        })}
      </TableWrapper>
      {hasNextPage && (
        <Flex mt={4} justifyContent={'center'}>
          <Button
            isLoading={loading || networkStatus === NetworkStatus.fetchMore}
            rounded={'xl'}
            onClick={handleLoadMore}
          >
            {t('common.load-more')}
          </Button>
        </Flex>
      )}
    </>
  )
}

const GenericGames = ({ address, game }: { address: string; game: Game }) => {
  const { t } = useTranslation()
  const { data, error, loading, fetchMore, networkStatus } = useQuery(USER_BET_HISTORY, {
    variables: { address, game: game === Game.LOOTBOX ? 'lootbox-unlimited' : game },
    notifyOnNetworkStatusChange: true,
  })

  const hasNextPage = data?.betsHistories?.pageInfo.hasNextPage

  const handleLoadMore = () => {
    if (hasNextPage && networkStatus !== NetworkStatus.refetch) {
      fetchMore({
        variables: {
          cursor: data?.betsHistories?.pageInfo.endCursor,
        },
      })
    }
  }

  const games = transformGames(data?.betsHistories)

  if (error) {
    return <Text>{t('common.error-fetching-games')}</Text>
  }

  if (networkStatus === NetworkStatus.loading || !address) {
    return <SkeletonStyledTable />
  }

  if (!games?.length) {
    return <NoResultsFallback text={t('messages.no-games')} />
  }

  return (
    <>
      <TableWrapper>
        {games.map((game, index) => {
          const isLastRow = index === games.length - 1
          return (
            <Tr key={game.gameId + '-' + game.betId}>
              {game.game == Game.GAMEZONE ? (
                <GameZoneRow isLastRow={isLastRow} {...game} />
              ) : (
                <GenericRow isLastRow={isLastRow} {...game} />
              )}
            </Tr>
          )
        })}
      </TableWrapper>
      {hasNextPage && (
        <Flex mt={4} justifyContent={'center'}>
          <Button
            isLoading={loading || networkStatus === NetworkStatus.fetchMore}
            isDisabled={!hasNextPage}
            rounded={'xl'}
            onClick={handleLoadMore}
          >
            {t('common.load-more')}
          </Button>
        </Flex>
      )}
    </>
  )
}

const transformGames = (data: GetUserGamesQuery['betsHistories']) => {
  return data?.edges.map(({ node }) => {
    let multiplier = new BigNumberInWei(node?.payout).dividedBy(node?.bet)
    let bet = {
      amount: node?.bet,
      denom: node?.denom,
    }

    return {
      bet: bet,
      game: node?.game,
      gameId: node?.gameId,
      betId: node?.betId,
      createdAt: node?.createdAt,
      multiplier: {
        isWon: multiplier.gte(1),
        number: multiplier.toFixed(2),
      },
      reward: {
        amount: node?.payout,
        denom: node?.denom,
      },
      widget: (
        <Text fontSize={'sm'} fontWeight={700} color={'ninja.200'}>
          #{node?.gameId}
        </Text>
      ),
      extra: node?.extra,
    } as UserBet
  })
}

import { groupBy } from '@/helpers/groupBy'
import { SHU_DENOM, USDC_NEW_DENOM } from '@/constants/constants'

const DoubleGames = ({ address }: { address: string }) => {
  const { t } = useTranslation()
  const { data, error, loading, fetchMore, networkStatus } = useQuery(LAST_ROULETTE_GAMES_QUERY, {
    variables: { address: address },
    notifyOnNetworkStatusChange: true,
  })

  const hasNextPage = data?.doubleUserGames?.pageInfo.hasNextPage

  const handleLoadMore = () => {
    if (hasNextPage && networkStatus !== NetworkStatus.refetch) {
      fetchMore({
        variables: {
          cursor: data?.doubleUserGames?.pageInfo.endCursor,
        },
      })
    }
  }

  const games = transformDoubleGames(data?.doubleUserGames?.edges, address)

  if (error) {
    return <Text>{t('common.error-fetching-games')}</Text>
  }

  if (networkStatus === NetworkStatus.loading || !address) {
    return <SkeletonStyledTable />
  }

  if (!games?.length) {
    return <NoResultsFallback text={t('messages.no-games')} />
  }

  return (
    <>
      <StyledTable games={games} />
      {hasNextPage && (
        <Flex mt={4} justifyContent={'center'}>
          <Button
            isLoading={loading || networkStatus === NetworkStatus.fetchMore}
            isDisabled={!hasNextPage}
            rounded={'xl'}
            onClick={handleLoadMore}
          >
            {t('common.load-more')}
          </Button>
        </Flex>
      )}
    </>
  )
}

const MinesGames = ({ address }: { address: string }) => {
  const { t } = useTranslation()
  const { data, error, loading, fetchMore, networkStatus } = useQuery(MINES_GAMES_HISTORY_QUERY, {
    variables: { address: address },
    notifyOnNetworkStatusChange: true,
  })

  const hasNextPage = data?.minesGames?.pageInfo.hasNextPage

  const handleLoadMore = () => {
    if (hasNextPage && networkStatus !== NetworkStatus.refetch) {
      fetchMore({
        variables: {
          cursor: data?.minesGames?.pageInfo.endCursor,
        },
      })
    }
  }

  const games = data?.minesGames?.edges.map(({ node }) => {
    const mutlipler = new BigNumberInWei(node?.payable).dividedBy(node?.bet)
    return {
      bet: {
        amount: node?.bet,
        denom: node?.denom,
      },
      betId: '0',
      game: Game.MINES,
      gameId: node?.id,
      multiplier: {
        isWon: mutlipler.gte(1),
        number: mutlipler.toFixed(2),
      },
      reward: {
        amount: node?.payable,
        denom: node?.denom,
      },
      widget: <MinesGameElement status={node?.status!} mines={node?.mines!} gemsOpened={node?.gemsOpened!} />,
    } as UserBet
  })

  if (error) {
    return <Text>{t('common.error-fetching-games')}</Text>
  }

  if (networkStatus === NetworkStatus.loading || !address) {
    return <SkeletonStyledTable />
  }

  if (!games?.length) {
    return <NoResultsFallback text={t('messages.no-games')} />
  }

  return (
    <>
      <TableWrapper>
        {games.map((game, index) => {
          const isLastRow = index === games.length - 1
          return (
            <Tr key={game.gameId + '-' + game.betId}>
              <GenericRow isLastRow={isLastRow} {...game} />
            </Tr>
          )
        })}
      </TableWrapper>
      {hasNextPage && (
        <Flex mt={4} justifyContent={'center'}>
          <Button
            isLoading={loading || networkStatus === NetworkStatus.fetchMore}
            isDisabled={!hasNextPage}
            rounded={'xl'}
            onClick={handleLoadMore}
          >
            {t('common.load-more')}
          </Button>
        </Flex>
      )}
    </>
  )
}
